import { ShopProductAttributeSource } from '@kanbu/schema/enums';
import { useCallback } from 'react';

import { trpc } from '@/services/trpc';

/**
 * The per-product attribute actions — add, correct, remove, restore — each of
 * which has to survive the next import.
 *
 * All of them are `source: manual` rows, which is what keeps them out of the
 * way of the two automatic writers: the importer only ever updates and sweeps
 * its own `feed` rows, and enrichment rewrites the values it owns on every run
 * and stops at manual ones. That stamp is the only reason an addition, a
 * correction or a removal outlives the next import. The API enforces it
 * server-side; the update calls send it too, to keep the intent visible at the
 * call site instead of hiding it behind a default. The create calls cannot
 * send it: it is not part of their contract at all, so an unprotected
 * hand-written value is unrepresentable rather than merely discouraged.
 *
 * A removal deactivates the row rather than deleting it, and is local to this
 * product: an absent row is indistinguishable from one that was never
 * extracted, so a delete would simply be re-added on the next import.
 * Switching an attribute off for the whole feed is a different action, on the
 * feed's attributes screen.
 */
export function useShopProductAttributeValueMutation() {
  const utils = trpc.useUtils();

  const invalidate = useCallback(
    async () =>
      Promise.all([
        utils.shopProducts.findOne.invalidate(),
        // Usage counts on the feed attributes screen are derived from these
        // rows, so adding or removing one here changes what that screen
        // reports — and adding an attribute changes the vocabulary it lists.
        utils.shopProductAttributes.listByFeed.invalidate(),
      ]),
    [utils],
  );

  const updateValue = trpc.shopProductAttributes.updateValue.useMutation({
    onSuccess: invalidate,
  });

  /** Attaches an attribute the feed's vocabulary already has. */
  const createValue = trpc.shopProductAttributes.createValue.useMutation({
    onSuccess: invalidate,
  });

  /**
   * Creates the vocabulary entry and its first value together. The definition
   * is feed-wide — the enrichment model is told to reuse it on the rest of the
   * catalog — while the value it carries lands on one product.
   */
  const createWithValue =
    trpc.shopProductAttributes.createWithValue.useMutation({
      onSuccess: invalidate,
    });

  const { mutateAsync } = updateValue;

  /**
   * `numericValue` is passed straight through, including `null`, and is
   * omitted for a non-numeric attribute so an edit there cannot clear a
   * number the form never showed. Nothing reconciles it with `value` — see
   * the contract's note on why an override the system can veto is not one.
   */
  const correctValue = useCallback(
    ({
      id,
      value,
      numericValue,
    }: {
      id: string;
      value: string;
      numericValue?: number | null;
    }) =>
      mutateAsync({
        id,
        value,
        ...(numericValue === undefined ? {} : { numericValue }),
        source: ShopProductAttributeSource.Manual,
      }),
    [mutateAsync],
  );

  const removeValue = useCallback(
    ({ id }: { id: string }) =>
      mutateAsync({
        id,
        active: false,
        source: ShopProductAttributeSource.Manual,
      }),
    [mutateAsync],
  );

  const restoreValue = useCallback(
    ({ id }: { id: string }) =>
      mutateAsync({
        id,
        active: true,
        source: ShopProductAttributeSource.Manual,
      }),
    [mutateAsync],
  );

  return {
    updateValue,
    createValue,
    createWithValue,
    correctValue,
    removeValue,
    restoreValue,
  };
}
